import React from 'react';
import { Leaf, Users, BookOpen, Award, Heart, Briefcase, Globe, TrendingUp, Linkedin, Instagram, Youtube } from 'lucide-react';
export function App() {
  return <div className="w-full min-h-screen bg-gradient-to-br from-green-50 via-cream-50 to-green-100" data-id="element-0">
      {/* Hero Section */}
      <section className="relative overflow-hidden" data-id="element-1">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200')] bg-cover bg-center opacity-5" data-id="element-2"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-32" data-id="element-3">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-4">
            <div className="space-y-8" data-id="element-5">
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight" data-id="element-6">
                Build Your Sustainability Career in the{' '}
                <span className="text-green-700" data-id="element-7">Global South</span>
              </h1>
              <p className="text-xl text-gray-700" data-id="element-8">
                Join a growing community helping changemakers start, grow, and
                thrive in environmental sustainability careers.
              </p>
              <a href="https://tinyurl.com/s-careerkit" target="_blank" rel="noopener noreferrer" className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl transition-all flex items-center gap-2 inline-flex" data-id="element-9">
                👉 Get the Free Sustainability Career Starter Kit
              </a>
            </div>
            <div className="relative" data-id="element-10">
              <div className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-green-600/20 rounded-3xl blur-3xl" data-id="element-11"></div>
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/ks6HiF9b7dzPL5BEFQ9VPa/Environmental_Sustainability_Expert_Bella_Ojoka.png" alt="Bella Ojoka" className="relative rounded-3xl shadow-2xl w-full object-cover aspect-square" data-id="element-12" />
            </div>
          </div>
        </div>
      </section>
      {/* About the Curator */}
      <section className="bg-white py-20" data-id="element-13">
        <div className="max-w-6xl mx-auto px-6" data-id="element-14">
          <div className="grid lg:grid-cols-3 gap-12 items-start" data-id="element-15">
            <div className="lg:col-span-1" data-id="element-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-17">
                Meet the Curator:
              </h2>
              <h3 className="text-3xl font-bold text-green-700" data-id="element-18">
                Uneojo Bella Ojoka
              </h3>
            </div>
            <div className="lg:col-span-2" data-id="element-19">
              <p className="text-lg text-gray-700 leading-relaxed" data-id="element-20">
                Bella Ojoka is an environmental consultant, writer, and youth
                development advocate passionate about helping young people in
                the Global South build impactful sustainability careers. With a
                background in Environmental Management and Toxicology and
                experience across environmental consulting, real estate, and
                instructional design, she bridges the gap between technical
                knowledge and practical career growth. Her mission: connect
                students, young professionals, and career transitioners with the
                mentors, knowledge, and opportunities they need to thrive.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Story Section */}
      <section className="bg-gradient-to-br from-green-50 to-cream-100 py-20" data-id="element-21">
        <div className="max-w-4xl mx-auto px-6" data-id="element-22">
          <div className="bg-white rounded-3xl shadow-xl p-10 lg:p-16" data-id="element-23">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-24">
              "I studied Environmental Management & Toxicology, but still had no
              idea how to start a sustainability career..."
            </h2>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-25">
              <p data-id="element-26">
                Bella's journey mirrors that of many aspiring sustainability
                professionals — passionate but unsure where to begin. Through
                mentorship, networking, and self-discovery, she connected the
                dots: the skills, certifications, and connections that make the
                field accessible.
              </p>
              <p data-id="element-27">
                Now, she's helping others bridge that same gap — because the
                demand for sustainability professionals in the Global South is
                growing by over 11% annually, and the world will need millions
                of new green workers by 2050.
              </p>
            </div>
            <a href="https://tinyurl.com/s-careerkit" target="_blank" rel="noopener noreferrer" className="mt-8 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl transition-all flex items-center gap-2 inline-flex" data-id="element-28">
              🌱 Download the Free Starter Kit
            </a>
          </div>
        </div>
      </section>
      {/* Inside the Starter Kit */}
      <section className="bg-white py-20" data-id="element-29">
        <div className="max-w-7xl mx-auto px-6" data-id="element-30">
          <h2 className="text-4xl lg:text-5xl font-bold text-center text-gray-900 mb-4" data-id="element-31">
            What You'll Learn
          </h2>
          <p className="text-center text-lg text-gray-600 mb-16 max-w-3xl mx-auto" data-id="element-32">
            Created especially for students, early-career professionals, and
            transitioners across Africa, Asia, and Latin America.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-33">
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow" data-id="element-34">
              <div className="bg-green-600 w-16 h-16 rounded-full flex items-center justify-center mb-6" data-id="element-35">
                <Leaf className="w-8 h-8 text-white" data-id="element-36" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-37">
                Understand Sustainability Careers
              </h3>
              <p className="text-gray-700" data-id="element-38">
                Explore key roles in renewable energy, waste management, ESG,
                and more.
              </p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow" data-id="element-39">
              <div className="bg-green-600 w-16 h-16 rounded-full flex items-center justify-center mb-6" data-id="element-40">
                <TrendingUp className="w-8 h-8 text-white" data-id="element-41" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-42">
                Build Experience Without a Job
              </h3>
              <p className="text-gray-700" data-id="element-43">
                Practical ways to grow your portfolio and credibility.
              </p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow" data-id="element-44">
              <div className="bg-green-600 w-16 h-16 rounded-full flex items-center justify-center mb-6" data-id="element-45">
                <Heart className="w-8 h-8 text-white" data-id="element-46" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-47">
                Rebrand for Green Roles
              </h3>
              <p className="text-gray-700" data-id="element-48">
                Update your CV, LinkedIn, and professional story.
              </p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow" data-id="element-49">
              <div className="bg-green-600 w-16 h-16 rounded-full flex items-center justify-center mb-6" data-id="element-50">
                <Award className="w-8 h-8 text-white" data-id="element-51" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-52">
                Access Free Certifications
              </h3>
              <p className="text-gray-700" data-id="element-53">
                Learn about credible, affordable credentials globally.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* The Community */}
      <section className="bg-gradient-to-br from-green-600 to-green-800 py-20 text-white" data-id="element-54">
        <div className="max-w-6xl mx-auto px-6" data-id="element-55">
          <h2 className="text-4xl lg:text-5xl font-bold text-center mb-6" data-id="element-56">
            Grow with a Community of Sustainability Professionals
          </h2>
          <p className="text-xl text-center mb-16 text-green-100 max-w-4xl mx-auto" data-id="element-57">
            The Networking Salon for Environmental Sustainability Professionals
            connects students, early-career changemakers, and transitioners with
            sustainability experts for mentorship, collaboration, and growth.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12" data-id="element-58">
            <div className="text-center" data-id="element-59">
              <div className="bg-white/20 backdrop-blur-sm w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-60">
                <Users className="w-10 h-10" data-id="element-61" />
              </div>
              <p className="font-semibold text-lg" data-id="element-62">
                🤝 Learn directly from professionals
              </p>
            </div>
            <div className="text-center" data-id="element-63">
              <div className="bg-white/20 backdrop-blur-sm w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-64">
                <Briefcase className="w-10 h-10" data-id="element-65" />
              </div>
              <p className="font-semibold text-lg" data-id="element-66">
                💼 Discover jobs and internships
              </p>
            </div>
            <div className="text-center" data-id="element-67">
              <div className="bg-white/20 backdrop-blur-sm w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-68">
                <BookOpen className="w-10 h-10" data-id="element-69" />
              </div>
              <p className="font-semibold text-lg" data-id="element-70">
                📚 Build in-demand green skills
              </p>
            </div>
            <div className="text-center" data-id="element-71">
              <div className="bg-white/20 backdrop-blur-sm w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-72">
                <Globe className="w-10 h-10" data-id="element-73" />
              </div>
              <p className="font-semibold text-lg" data-id="element-74">
                🌍 Collaborate on local projects
              </p>
            </div>
          </div>
          <div className="text-center" data-id="element-75">
            <a href="https://tinyurl.com/s-careerkit" target="_blank" rel="noopener noreferrer" className="bg-white text-green-700 hover:bg-green-50 px-8 py-4 rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl transition-all inline-block" data-id="element-76">
              ✨ Take the Survey & Join the Founding Circle
            </a>
          </div>
        </div>
      </section>
      {/* CTA Anchor */}
      <section className="bg-white py-20" data-id="element-77">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-78">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6" data-id="element-79">
            Help Shape the Future of Sustainability Networking in the Global
            South
          </h2>
          <p className="text-xl text-gray-700 mb-8 leading-relaxed" data-id="element-80">
            Before the full community launches, we want your voice to shape it.
            Take a short survey to receive your free Sustainability Career
            Starter Kit and share what kind of support you'd love to see.
          </p>
          <a href="https://tinyurl.com/s-careerkit" target="_blank" rel="noopener noreferrer" className="bg-green-600 hover:bg-green-700 text-white px-10 py-5 rounded-lg text-xl font-semibold shadow-xl hover:shadow-2xl transition-all inline-block" data-id="element-81">
            → Fill the short survey & get your free guide
          </a>
        </div>
      </section>
      {/* About The Networking Salon */}
      <section className="bg-gradient-to-br from-gray-50 to-green-50 py-20" data-id="element-82">
        <div className="max-w-5xl mx-auto px-6" data-id="element-83">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-8" data-id="element-84">
            About The Networking Salon
          </h2>
          <div className="bg-white rounded-3xl shadow-xl p-10 lg:p-16" data-id="element-85">
            <p className="text-lg text-gray-700 leading-relaxed mb-6" data-id="element-86">
              We are a High-Trust Ecosystem for Culture Shapers — curating
              salon-style networking experiences that power ideas, retool people
              for growth, and curate opportunities for impact.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6" data-id="element-87">
              Through intimate events, curated communities, and collaborative
              spaces, we help professionals and organizations connect
              meaningfully, exchange value, and create lasting change across
              industries.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed" data-id="element-88">
              The Sustainability Professionals Salon is one of our niche
              communities designed to empower emerging environmental leaders
              across the Global South to build influence, credibility, and
              impact.
            </p>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12" data-id="element-89">
        <div className="max-w-7xl mx-auto px-6" data-id="element-90">
          <div className="grid md:grid-cols-3 gap-12 mb-8" data-id="element-91">
            <div data-id="element-92">
              <h3 className="text-2xl font-bold mb-2" data-id="element-93">The Networking Salon</h3>
              <p className="text-gray-400" data-id="element-94">
                A High-Trust Ecosystem for Culture Shapers
              </p>
            </div>
            <div data-id="element-95">
              <h4 className="font-semibold mb-4" data-id="element-96">Quick Links</h4>
              <ul className="space-y-2 text-gray-400" data-id="element-97">
                <li data-id="element-98">
                  <a href="#" className="hover:text-white transition-colors" data-id="element-99">
                    About
                  </a>
                </li>
                <li data-id="element-100">
                  <a href="#" className="hover:text-white transition-colors" data-id="element-101">
                    Join the Community
                  </a>
                </li>
                <li data-id="element-102">
                  <a href="#" className="hover:text-white transition-colors" data-id="element-103">
                    Events
                  </a>
                </li>
                <li data-id="element-104">
                  <a href="#" className="hover:text-white transition-colors" data-id="element-105">
                    Contact
                  </a>
                </li>
              </ul>
            </div>
            <div data-id="element-106">
              <h4 className="font-semibold mb-4" data-id="element-107">Connect With Us</h4>
              <div className="flex gap-4" data-id="element-108">
                <a href="#" className="bg-gray-800 hover:bg-gray-700 p-3 rounded-full transition-colors" data-id="element-109">
                  <Linkedin className="w-5 h-5" data-id="element-110" />
                </a>
                <a href="#" className="bg-gray-800 hover:bg-gray-700 p-3 rounded-full transition-colors" data-id="element-111">
                  <Instagram className="w-5 h-5" data-id="element-112" />
                </a>
                <a href="#" className="bg-gray-800 hover:bg-gray-700 p-3 rounded-full transition-colors" data-id="element-113">
                  <Youtube className="w-5 h-5" data-id="element-114" />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400" data-id="element-115">
            <p data-id="element-116">© 2025 The Networking Salon Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>;
}